/**
 * Installing Namu, and the service worker behind it.
 *
 * Namu is meant to be launched from a home screen and talked to, so the install
 * path matters more than it would for a page people bookmark. Two browsers
 * behave very differently here: Chrome fires `beforeinstallprompt` and lets the
 * app choose the moment to ask, while iOS Safari never fires it at all and the
 * only route is Share, then Add to Home Screen. Everything below is written so
 * the second case, no event, ever, is the normal case rather than an error.
 */

const SERVICE_WORKER_URL = '/sw.js'

/**
 * The saved install event. It is not in lib.dom because it is not standardised,
 * and `userChoice` is optional here because a browser that fires the event is
 * not obliged to hand us one.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice?: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

export interface InstallPrompt {
  prompt(): Promise<void>
  dismissed: boolean
}

/**
 * Registers the service worker in production, and actively removes it in dev.
 *
 * The unregister is not tidiness. A production build previewed on localhost
 * leaves a worker registered for that origin, and `localhost:5173` is the same
 * origin the dev server uses, so the stale worker keeps serving its cached shell
 * over every subsequent `npm run dev`. Edits appear to do nothing, and the cause
 * is invisible from the terminal. Clearing it here costs one call and removes a
 * whole category of lost afternoons.
 */
export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return

  if (import.meta.env.DEV) {
    void navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => Promise.all(registrations.map((one) => one.unregister())))
      .catch((error) => {
        console.warn('Namu: could not clear old service workers', error)
      })
    return
  }

  const register = (): void => {
    navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' }).catch((error) => {
      // A failed registration means no offline support, which is a downgrade,
      // not a breakage. It must never take the app down with it.
      console.warn('Namu: service worker registration failed', error)
    })
  }

  // Waiting for load keeps the worker install off the critical path. On a slow
  // phone the first paint and the first microphone prompt are competing for the
  // same main thread, and neither should lose to a cache warm-up.
  if (typeof window === 'undefined') return
  if (document.readyState === 'complete') register()
  else window.addEventListener('load', register, { once: true })
}

/** Whether Namu is running as an installed app rather than in a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false

  // iOS Safari predates display-mode and still reports through this one.
  if (
    typeof navigator !== 'undefined' &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  ) {
    return true
  }

  if (typeof window.matchMedia !== 'function') return false
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches
    )
  } catch {
    return false
  }
}

/**
 * Watches for the browser's install offer and hands it back as something the UI
 * can hold.
 *
 * `onAvailable` is called with null when the offer goes away, either because it
 * was used or because the app was installed. On iOS it is simply never called,
 * so every caller has to treat null as a permanent state, not a loading one.
 */
export function watchInstallPrompt(
  onAvailable: (prompt: InstallPrompt | null) => void,
): () => void {
  if (typeof window === 'undefined') return () => {}

  let saved: BeforeInstallPromptEvent | null = null

  const handleBeforeInstallPrompt = (event: Event): void => {
    // Without preventDefault Chrome shows its own mini-infobar. Namu asks in its
    // own words, at a point in the conversation where the offer makes sense.
    event.preventDefault()
    const captured = event as BeforeInstallPromptEvent
    saved = captured

    const handle: InstallPrompt = {
      dismissed: false,
      async prompt(): Promise<void> {
        // The event is single use. Dropping the reference first means a double
        // tap cannot ask twice and throw.
        if (saved !== captured) return
        saved = null
        try {
          await captured.prompt()
          const choice = await captured.userChoice
          handle.dismissed = choice?.outcome === 'dismissed'
        } catch (error) {
          console.warn('Namu: install prompt could not be shown', error)
          handle.dismissed = true
        }
        onAvailable(null)
      },
    }

    onAvailable(handle)
  }

  const handleInstalled = (): void => {
    saved = null
    onAvailable(null)
  }

  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.addEventListener('appinstalled', handleInstalled)

  return () => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.removeEventListener('appinstalled', handleInstalled)
  }
}
